import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../utils/platform';
import { getSubscriptionStatus, sendSubscriptionReminder, getFundCategories, getChurchProfile } from '../../services/api';
import { FiPrinter, FiMessageCircle, FiBell, FiCheckCircle, FiSend, FiCopy, FiDownload, FiX } from 'react-icons/fi';
import Toast from '../../components/Toast';
import { useAuth } from '../../context/AuthContext';
import { openPreview } from '../../utils/printHtml';

const STATUS_PILL = { on_time: 'pill-green', due: 'pill-yellow', overdue: 'pill-red', no_period: 'pill-gray' };
const STATUS_LABEL = { on_time: 'On time', due: 'Due', overdue: 'Overdue', no_period: 'No period' };

const fmtMoney = (v, prefix) => {
  const n = parseFloat(v || 0);
  return n > 0 ? `${prefix}${n.toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '—';
};

const SubscriptionStatus = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [funds, setFunds] = useState([]);
  const [profile, setProfile] = useState({});
  const [fundFilter, setFundFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('action');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);

  // v1.9.57 — Broadcast Helper: prep a copy-and-paste bundle for the church
  // phone's WhatsApp Broadcast List. No Cloud API needed, so no dedicated
  // SIM required. Composes a single generic message + copies phone numbers
  // + exports a .vcf so the church phone recognises members as saved
  // contacts (WhatsApp broadcast lists silently drop recipients that don't
  // have the sender's number saved — .vcf fixes both directions).
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [broadcastExcluded, setBroadcastExcluded] = useState(new Set());
  // v1.9.98 — row-level selection for "Send WhatsApp to selected". Stores
  // the phone numbers of ticked rows. Opens the same broadcast modal with
  // broadcastExcluded pre-populated to everyone EXCEPT the ticked rows.
  const [selectedPhones, setSelectedPhones] = useState(new Set());

  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 3000); };

  const load = () => {
    getSubscriptionStatus({ fund_id: fundFilter, status: statusFilter, from, to })
      .then(r => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
  };

  useEffect(() => { load(); }, [fundFilter, statusFilter, from, to]);
  useEffect(() => {
    getFundCategories().then(r => setFunds(Array.isArray(r.data) ? r.data.filter(f => f.frequency) : [])).catch(() => {});
    getChurchProfile().then(r => setProfile(r.data || {})).catch(() => {});
  }, []);

  // v1.8.6 — Client-side search across member name, family-head/spiritual father,
  // and phone. Case- and space-insensitive; splits the query so "sirak michael"
  // matches both terms in any order.
  const filteredRows = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    const terms = q.split(/\s+/).filter(Boolean);
    return rows.filter(r => {
      const hay = [r.member_name, r.spiritual_father_name, r.member_phone, r.fund_name]
        .filter(Boolean).join(' ').toLowerCase();
      return terms.every(t => hay.includes(t));
    });
  })();

  const counts = filteredRows.reduce((acc, r) => { acc[r.derived_status] = (acc[r.derived_status] || 0) + 1; return acc; }, {});
  const currencies = (profile.currencies && profile.currencies.length) ? profile.currencies : ['$', 'K'];
  const cur1 = currencies[0] || '$';
  const cur2 = currencies[1] || 'K';
  // v1.6.13 — Per-currency total-owed across the filtered set.
  // Uses total_owed_* from the backend (which subtracts partial payments)
  // instead of pledge x period_count, so partials reduce the figure correctly.
  const overdueByCurrency = currencies.reduce((acc, sym) => {
    acc[sym] = filteredRows.reduce((s, r) => {
      const owed = sym === '$' ? parseFloat(r.total_owed_usd || 0)
                 : sym === 'K' ? parseFloat(r.total_owed_kwacha || 0)
                 : 0;
      return s + (owed > 0 ? owed : 0);
    }, 0);
    return acc;
  }, {});

  // v1.9.90 — reminder message rewrite.
  // Old message showed only the monthly pledge amount, so a member owing
  // K2,000 for 10 months would still see "K200" and think they're only
  // one period behind. New message shows the real total outstanding AND the
  // number of overdue periods so the member can see the full picture.
  // Signature is template-driven (`profile.reminder_signature`) so the
  // church can edit it from the Church Profile settings page; default is
  // "Admin, Magabe Haymanot Teklom" per 2026-10-09 spec.
  const buildMessage = (r) => {
    const owedParts = [];
    const usd = parseFloat(r.total_owed_usd || 0);
    const k   = parseFloat(r.total_owed_kwacha || 0);
    if (usd > 0) owedParts.push(`$${usd.toFixed(2)}`);
    if (k   > 0) owedParts.push(`K${k.toFixed(2)}`);
    const owedStr = owedParts.join(' + ') || '—';
    const periodCount = parseInt(r.overdue_period_count || r.unpaid_period_count || 0, 10) || 0;
    const periodWord = r.frequency === 'annually' ? 'year' : r.frequency === 'quarterly' ? 'quarter' : r.frequency === 'weekly' ? 'week' : 'month';
    const periodStr = periodCount > 0 ? `${periodCount} ${periodWord}${periodCount === 1 ? '' : 's'}` : '';

    const lines = [];
    lines.push(`Dear ${r.member_name || 'Member'},`);
    lines.push('');
    lines.push(`This is a friendly reminder from ${profile.church_name || 'the church'}.`);
    lines.push('');
    if (r.derived_status === 'overdue') {
      lines.push(`Your ${r.fund_name || 'contribution'} is in arrears.`);
      lines.push(`Total outstanding: *${owedStr}*${periodStr ? ` (${periodStr} overdue)` : ''}.`);
      lines.push(`First unpaid period due: ${r.current_due_date || '—'} (${r.days_overdue} day${r.days_overdue === 1 ? '' : 's'} ago).`);
      lines.push('Please settle at your earliest convenience.');
    } else if (r.derived_status === 'due') {
      lines.push(`Your ${r.fund_name || 'contribution'} is due.`);
      lines.push(`Amount outstanding: *${owedStr}*${periodStr ? ` (${periodStr})` : ''}.`);
      lines.push(`Due date: ${r.current_due_date || '—'}.`);
    } else {
      lines.push(`A reminder for your ${r.fund_name || 'contribution'}.`);
      if (owedParts.length) lines.push(`Amount outstanding: *${owedStr}*.`);
    }
    lines.push('');
    lines.push('May God bless you for your faithfulness.');
    lines.push('');
    const signature = (profile.reminder_signature && String(profile.reminder_signature).trim())
      || 'Admin, Magabe Haymanot Teklom';
    lines.push(signature);
    return lines.join('\n');
  };

  const sendWhatsApp = async (r) => {
    if (!r.member_phone) { showToast('No phone number on file for this member', 'error'); return; }
    const message = buildMessage(r);
    setBusy(true);
    try {
      await sendSubscriptionReminder({
        subscription_id: r.subscription_id,
        period_id: r.current_period_id,
        method: 'whatsapp',
        message,
      });
      const phone = String(r.member_phone).replace(/[^\d]/g, '');
      const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
      window.open(url, '_blank');
      showToast('Reminder logged. Tap Send in WhatsApp.');
      load();
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
    finally { setBusy(false); }
  };

  const markReminded = async (r) => {
    setBusy(true);
    try {
      await sendSubscriptionReminder({
        subscription_id: r.subscription_id,
        period_id: r.current_period_id,
        method: 'in_app',
        message: buildMessage(r),
      });
      showToast('Marked as reminded — member will see a banner on next login.');
      load();
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
    finally { setBusy(false); }
  };

  // Dedupe overdue rows down to unique members. One person = one entry in
  // the broadcast list even if they have 3 overdue subscriptions.
  const overdueMembers = (() => {
    const byId = new Map();
    for (const r of filteredRows) {
      if (r.derived_status !== 'overdue' && r.derived_status !== 'due') continue;
      if (!r.member_phone) continue; // no phone = can't broadcast
      const key = r.member_id || `${r.member_name}|${r.member_phone}`;
      const bucket = byId.get(key) || {
        member_id: r.member_id,
        member_name: r.member_name,
        member_phone: r.member_phone,
        funds: new Set(),
        owed_usd: 0,
        owed_kwacha: 0,
      };
      bucket.funds.add(r.fund_name || '');
      bucket.owed_usd += parseFloat(r.total_owed_usd || 0);
      bucket.owed_kwacha += parseFloat(r.total_owed_kwacha || 0);
      byId.set(key, bucket);
    }
    return Array.from(byId.values()).map(b => ({
      ...b,
      funds: Array.from(b.funds).filter(Boolean).join(', '),
    }));
  })();

  const activeBroadcast = overdueMembers.filter(m => !broadcastExcluded.has(m.member_phone));

  const defaultBroadcastMessage = () => {
    const church = profile.church_name || 'St. Michael Orthodox Church';
    return [
      `Dear brother/sister in Christ,`,
      ``,
      `This is a friendly reminder from ${church}. You currently have overdue church contributions.`,
      `Please make your payment when convenient, or reach out to us if you need to discuss.`,
      ``,
      `May God bless you for your faithfulness.`,
      ``,
      `— ${church}`,
    ].join('\n');
  };

  const openBroadcast = () => {
    setBroadcastMessage(defaultBroadcastMessage());
    setBroadcastExcluded(new Set());
    setBroadcastOpen(true);
  };

  // v1.9.98 — open the same modal but only with the ticked rows kept active.
  const openBroadcastSelected = () => {
    if (selectedPhones.size === 0) { showToast('Pick at least one row first', 'error'); return; }
    setBroadcastMessage(defaultBroadcastMessage());
    const excl = new Set(
      overdueMembers.map(m => m.member_phone).filter(p => !selectedPhones.has(p))
    );
    setBroadcastExcluded(excl);
    setBroadcastOpen(true);
  };

  const toggleRowSelect = (phone) => {
    if (!phone) return;
    setSelectedPhones(prev => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone); else next.add(phone);
      return next;
    });
  };
  const selectAllVisible = () => {
    const visiblePhones = filteredRows
      .filter(r => r.member_phone && (r.derived_status === 'overdue' || r.derived_status === 'due'))
      .map(r => r.member_phone);
    setSelectedPhones(new Set(visiblePhones));
  };
  const clearSelection = () => setSelectedPhones(new Set());

  const toggleExclude = (phone) => {
    setBroadcastExcluded(prev => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone); else next.add(phone);
      return next;
    });
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast(`${label} copied to clipboard`);
    } catch { showToast('Copy failed — long-press the field instead', 'error'); }
  };

  const copyMessage = () => copyToClipboard(broadcastMessage, 'Message');

  const copyPhoneNumbers = () => {
    const list = activeBroadcast.map(m => String(m.member_phone).replace(/\s+/g, '')).join(', ');
    copyToClipboard(list, `${activeBroadcast.length} phone number${activeBroadcast.length === 1 ? '' : 's'}`);
  };

  // Build a single vCard 3.0 file with every overdue member as a contact.
  // Church admin imports it into the phone → WhatsApp instantly recognises
  // the members as saved contacts → broadcast list will reach them all.
  const downloadVcf = () => {
    if (activeBroadcast.length === 0) { showToast('No members to export', 'error'); return; }
    const escape = (s) => String(s || '').replace(/[\\;,]/g, m => '\\' + m).replace(/\n/g, '\\n');
    const vcf = activeBroadcast.map(m => {
      const name = escape(m.member_name || 'Church Member');
      const phone = String(m.member_phone).replace(/\s+/g, '');
      return [
        'BEGIN:VCARD',
        'VERSION:3.0',
        `FN:${name}`,
        `N:${name};;;;`,
        `TEL;TYPE=CELL:${phone}`,
        `NOTE:${escape((profile.church_name || 'Church') + ' member')}`,
        'END:VCARD',
      ].join('\r\n');
    }).join('\r\n');
    const blob = new Blob([vcf], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `church-overdue-${stamp}.vcf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    showToast(`Downloaded ${activeBroadcast.length} contact${activeBroadcast.length === 1 ? '' : 's'} as .vcf`);
  };

  const handlePrint = () => {
    const headerLines = [profile.church_address, profile.church_phone, profile.church_email].filter(Boolean).join(' · ');
    const dateRange = (from || to) ? `${from || 'all'} → ${to || 'all'}` : 'All dates';
    const fundLabel = fundFilter === 'all' ? 'All funds' : (funds.find(f => String(f.id) === String(fundFilter))?.name || '');
    const statusLabel = statusFilter === 'action' ? 'Needs attention (Due + Overdue)'
      : statusFilter === 'all' ? 'All statuses' : STATUS_LABEL[statusFilter];
    const tableRows = filteredRows.map((r, i) => {
      const usd = parseFloat(r.total_owed_usd || 0);
      const k   = parseFloat(r.total_owed_kwacha || 0);
      const owedParts = [usd > 0 ? `$${usd.toFixed(2)}` : null, k > 0 ? `K${k.toFixed(2)}` : null].filter(Boolean);
      const owedStr = owedParts.length ? owedParts.join(' · ') : '—';
      return `
      <tr>
        <td>${i + 1}</td>
        <td><strong>${r.member_name || '—'}</strong></td>
        <td>${r.member_phone || '—'}</td>
        <td>${r.fund_name || '—'}</td>
        <td>${r.frequency || '—'}</td>
        <td style="font-weight:700">${owedStr}</td>
        <td><span style="text-transform:capitalize;font-weight:700">${STATUS_LABEL[r.derived_status] || r.derived_status}</span>${r.derived_status === 'overdue' ? ` (${r.days_overdue}d)` : ''}</td>
        <td>${r.start_date || '—'}</td>
        <td>${r.current_due_date || '—'}</td>
        <td>${r.last_paid_date || '—'}</td>
      </tr>
    `;}).join('');
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Subscription Status</title>
      <style>
        @page { size: A4; margin: 10mm; }
        body { font-family: Arial, sans-serif; color: #111827; font-size: 12px; }
        h1 { font-size: 20px; text-align: center; color: #1e1b4b; margin: 0; }
        .contact { text-align: center; font-size: 10px; color: #6b7280; margin-top: 3px; }
        h2 { font-size: 14px; margin: 14px 0 6px; letter-spacing: 0.05em; }
        .meta { font-size: 11px; color: #6b7280; margin-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #f3f4f6; text-align: left; padding: 6px 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px; }
        td { padding: 6px 8px; border-bottom: 1px solid #f3f4f6; font-size: 11px; }
        tr:nth-child(even) td { background: #fafafa; }
      </style>
      </head><body>
        <h1>${profile.church_name || 'My Church'}</h1>
        ${headerLines ? `<div class="contact">${headerLines}</div>` : ''}
        <h2>SUBSCRIPTION STATUS REPORT</h2>
        <div class="meta">${fundLabel} · ${statusLabel} · ${dateRange}</div>
        <table>
          <thead><tr><th>#</th><th>Member</th><th>Phone</th><th>Fund</th><th>Frequency</th><th>Total Owed</th><th>Status</th><th>Start Date</th><th>Due Date</th><th>Last Paid</th></tr></thead>
          <tbody>${tableRows || '<tr><td colspan="10" style="text-align:center;padding:20px;color:#9ca3af">No records</td></tr>'}</tbody>
        </table>
      </body></html>`;
    openPreview(html);
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>Subscription Status</h1><p>{filteredRows.length} subscriptions{search && rows.length !== filteredRows.length ? ` of ${rows.length}` : ''}</p></div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* v1.9.98 — tick rows in the table to enable this button. Opens
              the broadcast modal pre-filtered to just the selected rows. */}
          {selectedPhones.size > 0 && (
            <button
              className="btn btn-primary"
              onClick={openBroadcastSelected}
              title="Send WhatsApp to the ticked rows only"
            >
              <FiSend /> Send to selected ({selectedPhones.size})
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={openBroadcast}
            disabled={overdueMembers.length === 0}
            title="Prepare a WhatsApp broadcast for all overdue members"
          >
            <FiSend /> Broadcast all ({overdueMembers.length})
          </button>
          {!isMobile && (
            <button className="btn btn-secondary" onClick={handlePrint} disabled={filteredRows.length === 0}>
              <FiPrinter /> {t('recordAttendance.print')}
            </button>
          )}
        </div>
      </div>

      {/* Stat counters */}
      <div className="stat-cards" style={{ marginBottom: 16 }}>
        <div className="stat-card"><div className="stat-icon" style={{ background: '#dcfce7', color: '#16a34a' }}><FiCheckCircle /></div>
          <div><div className="stat-label">On time</div><div className="stat-value" style={{ color: '#16a34a' }}>{counts.on_time || 0}</div></div></div>
        <div className="stat-card"><div className="stat-icon" style={{ background: '#fef9c3', color: '#a16207' }}><FiBell /></div>
          <div><div className="stat-label">Due</div><div className="stat-value" style={{ color: '#a16207' }}>{counts.due || 0}</div></div></div>
        <div className="stat-card"><div className="stat-icon" style={{ background: '#fee2e2', color: '#dc2626' }}><FiBell /></div>
          <div><div className="stat-label">Overdue</div><div className="stat-value" style={{ color: '#dc2626' }}>{counts.overdue || 0}</div></div></div>
        <div className="stat-card"><div className="stat-icon" style={{ background: '#dbeafe', color: '#2563eb' }}>{cur1}</div>
          <div><div className="stat-label">Overdue Pledge</div><div className="stat-value" style={{ fontSize: 16 }}>
            {(() => {
              const parts = Object.entries(overdueByCurrency)
                .filter(([, v]) => v > 0)
                .map(([sym, v]) => `${sym}${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
              return parts.length ? parts.join(' + ') : '—';
            })()}
          </div></div></div>
      </div>

      <div className="filters-row">
        <input
          className="filter-input"
          type="search"
          placeholder={t('common.searchNamePhone', { defaultValue: 'Search name, phone, family head…' })}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ minWidth: 240, flex: '1 1 240px' }}
        />
        <select className="filter-select" value={fundFilter} onChange={e => setFundFilter(e.target.value)}>
          <option value="all">All funds</option>
          {funds.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
        </select>
        <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="action">Needs attention (Due + Overdue)</option>
          <option value="all">All statuses</option>
          <option value="on_time">On time</option>
          <option value="due">Due</option>
          <option value="overdue">Overdue</option>
        </select>
        <label style={{ fontSize: 13, color: '#6b7280' }}>Due from</label>
        <input className="filter-input" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <label style={{ fontSize: 13, color: '#6b7280' }}>to</label>
        <input className="filter-input" type="date" value={to} onChange={e => setTo(e.target.value)} />
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                {/* v1.9.98 — header checkbox: tick all / clear all for the
                    currently visible due+overdue rows that have a phone number. */}
                <input
                  type="checkbox"
                  onChange={e => e.target.checked ? selectAllVisible() : clearSelection()}
                  checked={selectedPhones.size > 0 && selectedPhones.size === filteredRows.filter(r => r.member_phone && (r.derived_status === 'overdue' || r.derived_status === 'due')).length}
                  title="Select all overdue / due"
                />
              </th>
              <th>#</th><th>Member</th><th>Phone</th><th>Fund</th><th>Pledge</th>
              <th>Total Owed</th>
              <th>Status</th><th>Start Date</th><th>Due Date</th><th>Last Paid</th><th>Last Reminded</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr><td colSpan={13}><div className="empty-state"><p>No subscriptions match these filters</p></div></td></tr>
            ) : filteredRows.map((r, i) => (
              <tr key={r.subscription_id} style={{ background: r.derived_status === 'overdue' ? '#fff1f2' : 'transparent' }}>
                <td data-label="Select">
                  {r.member_phone && (r.derived_status === 'overdue' || r.derived_status === 'due') ? (
                    <input
                      type="checkbox"
                      checked={selectedPhones.has(r.member_phone)}
                      onChange={() => toggleRowSelect(r.member_phone)}
                    />
                  ) : null}
                </td>
                <td data-label="#">{i + 1}</td>
                <td data-label="Member">
                  <strong>{r.member_name}</strong>
                  {r.spiritual_father_name && <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.spiritual_father_name}</div>}
                </td>
                <td data-label="Phone" style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.member_phone || '—'}</td>
                <td data-label="Fund">{r.fund_name}</td>
                <td data-label="Pledge" style={{ fontSize: 12 }}>
                  {(() => {
                    const entries = Object.entries(r.pledges || {}).filter(([, v]) => parseFloat(v) > 0);
                    return entries.length
                      ? entries.map(([sym, v]) => <div key={sym}>{fmtMoney(v, sym)}</div>)
                      : '—';
                  })()}
                </td>
                {/* v1.6.13 — total owed = SUM(max(0, pledge − amount_paid)) across
                    all unpaid periods. Respects partials (v1.6.8). */}
                <td data-label="Total Owed" style={{ fontSize: 13, fontWeight: 700 }}>
                  {(() => {
                    const usd = parseFloat(r.total_owed_usd || 0);
                    const k   = parseFloat(r.total_owed_kwacha || 0);
                    if (usd <= 0 && k <= 0) return <span style={{ color: '#16a34a' }}>—</span>;
                    return (
                      <div style={{ color: r.derived_status === 'overdue' ? '#b91c1c' : '#b45309' }}>
                        {usd > 0 && <div>{fmtMoney(usd, '$')}</div>}
                        {k   > 0 && <div>{fmtMoney(k,   'K')}</div>}
                      </div>
                    );
                  })()}
                </td>
                <td data-label="Status">
                  <span className={`pill ${STATUS_PILL[r.derived_status]}`}>{STATUS_LABEL[r.derived_status]}</span>
                  {r.derived_status === 'overdue' && (
                    <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>
                      {r.days_overdue}d late
                      {r.overdue_period_count > 1 && ` · ${r.overdue_period_count} unpaid`}
                    </div>
                  )}
                  {r.derived_status === 'due' && r.unpaid_period_count > 1 && (
                    <div style={{ fontSize: 11, color: '#a16207', marginTop: 2 }}>{r.unpaid_period_count} unpaid</div>
                  )}
                </td>
                <td data-label="Start Date" style={{ fontSize: 12, color: '#7c3aed', fontWeight: 700 }}>{r.start_date || '—'}</td>
                <td data-label="Due Date">{r.current_due_date || '—'}</td>
                <td data-label="Last Paid">{r.last_paid_date || <span style={{ color: '#9ca3af' }}>Never</span>}</td>
                <td data-label="Last Reminded">
                  {r.last_reminded_at ? (
                    <div style={{ fontSize: 11 }}>
                      <div>{r.last_reminded_at.slice(0, 16)}</div>
                      <div style={{ color: '#9ca3af', textTransform: 'capitalize' }}>{r.last_reminded_by_method}</div>
                    </div>
                  ) : '—'}
                </td>
                <td data-label="Actions">
                  <div className="action-btns">
                    <button className="btn btn-sm btn-success" disabled={busy || !r.member_phone} onClick={() => sendWhatsApp(r)} title="Send WhatsApp reminder">
                      <FiMessageCircle /> WhatsApp
                    </button>
                    <button className="btn btn-sm btn-secondary" disabled={busy} onClick={() => markReminded(r)} title="Log reminder + show banner on member's next login">
                      <FiBell /> Mark Reminded
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {broadcastOpen && (
        <div
          onClick={() => setBroadcastOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.55)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}
          >
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>📢 Broadcast to overdue members</h3>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                  Prep a WhatsApp broadcast — copy on this device, send from the church phone.
                </div>
              </div>
              <button
                onClick={() => setBroadcastOpen(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 20, padding: 4 }}
                aria-label="Close"
              ><FiX /></button>
            </div>

            <div style={{ padding: 18, overflowY: 'auto', flex: 1 }}>
              {/* Message editor */}
              <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
                Message
              </label>
              <textarea
                value={broadcastMessage}
                onChange={e => setBroadcastMessage(e.target.value)}
                rows={7}
                style={{ width: '100%', padding: 10, border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, lineHeight: 1.5, fontFamily: 'inherit', resize: 'vertical' }}
              />
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                {broadcastMessage.length} characters — WhatsApp broadcast lists send the SAME message to every recipient. Personalise the wording if you want, then Copy Message below.
              </div>

              {/* Recipients */}
              <div style={{ marginTop: 16, display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Recipients ({activeBroadcast.length} of {overdueMembers.length})
                </label>
                {broadcastExcluded.size > 0 && (
                  <button
                    onClick={() => setBroadcastExcluded(new Set())}
                    style={{ background: 'none', border: 'none', color: '#4f46e5', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}
                  >Reset selections</button>
                )}
              </div>
              <div style={{ marginTop: 8, border: '1px solid #e5e7eb', borderRadius: 8, maxHeight: 240, overflowY: 'auto' }}>
                {overdueMembers.length === 0 ? (
                  <div style={{ padding: 16, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                    No overdue members with a phone number. Change filters or wait for the next period.
                  </div>
                ) : overdueMembers.map((m, i) => {
                  const excluded = broadcastExcluded.has(m.member_phone);
                  const owedParts = [
                    m.owed_usd > 0.001 ? `$${m.owed_usd.toFixed(2)}` : null,
                    m.owed_kwacha > 0.001 ? `K${m.owed_kwacha.toFixed(2)}` : null,
                  ].filter(Boolean).join(' · ');
                  return (
                    <label
                      key={`${m.member_phone}-${i}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '9px 12px', borderBottom: i < overdueMembers.length - 1 ? '1px solid #f3f4f6' : 'none',
                        cursor: 'pointer', opacity: excluded ? 0.45 : 1,
                        background: excluded ? '#f9fafb' : '#fff',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={!excluded}
                        onChange={() => toggleExclude(m.member_phone)}
                        style={{ width: 16, height: 16, cursor: 'pointer' }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', textDecoration: excluded ? 'line-through' : 'none' }}>
                          {m.member_name || 'Unnamed'}
                        </div>
                        <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
                          {m.member_phone}{m.funds && ` · ${m.funds}`}
                        </div>
                      </div>
                      {owedParts && (
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#b91c1c', whiteSpace: 'nowrap' }}>
                          {owedParts}
                        </div>
                      )}
                    </label>
                  );
                })}
              </div>

              {/* Steps hint */}
              <div style={{ marginTop: 16, padding: 12, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 8, fontSize: 12, color: '#3730a3', lineHeight: 1.6 }}>
                <strong>How to send:</strong><br />
                1. Tap <b>Download .vcf</b> → import into the church phone once so WhatsApp recognises members as saved contacts.<br />
                2. Tap <b>Copy Message</b> → open WhatsApp on the church phone → <b>New broadcast</b> → pick the imported contacts → paste message → Send.<br />
                <em style={{ color: '#4338ca' }}>Recipients only receive if they have the church's number saved on their side too.</em>
              </div>
            </div>

            <div style={{ padding: 14, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={downloadVcf}
                disabled={activeBroadcast.length === 0}
              ><FiDownload /> Download .vcf</button>
              <button
                className="btn btn-secondary"
                onClick={copyPhoneNumbers}
                disabled={activeBroadcast.length === 0}
              ><FiCopy /> Copy Numbers</button>
              <button
                className="btn btn-primary"
                onClick={copyMessage}
                disabled={!broadcastMessage.trim()}
              ><FiCopy /> Copy Message</button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast?.msg} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
};

export default SubscriptionStatus;
